import { useEffect, useRef, useState } from "react";
import KickClient from "~/visits/utils/api";
import CheckoutStatusCard, {
  StatusButton,
  StatusLinkButton,
} from "./checkoutStatusCard";
import {
  finishKlarnaCheckout,
  KLARNA_CHECKOUT_CANCELED,
  KLARNA_CHECKOUT_COMPLETE,
  KLARNA_CHECKOUT_EXPIRED,
  KLARNA_CHECKOUT_FAILED,
  KLARNA_CHECKOUT_PENDING,
} from "./klarnaCheckout";

// How the patient arrived at a pending attempt.
//
// "return": Stripe just sent them back from Klarna (or confirmSetup resolved
// in place), so an authorization may still be landing. Poll /resume briefly,
// then give up through the verified cancel path.
//
// "resume": they navigated here themselves — browser back from Klarna, a
// reopened tab, "Change payment method" — so the attempt is abandoned. One
// /cancel call: the server finalizes an authorization that did complete and
// cancels an unfinished one, so a late Klarna callback can never submit a
// second order after the patient pays another way.
export const KLARNA_RETURN_MODE = "return";
export const KLARNA_RESUME_MODE = "resume";
export const KLARNA_RETURN_POLLS = 6;
export const KLARNA_RETURN_POLL_MS = 1500;

// Shown above the payment form once the attempt is settled. Patient-facing:
// no "attempt", "authorization" or "setup intent".
export const KLARNA_NOTICES = {
  left: "You left Klarna before finishing. Choose a payment method below to continue.",
  canceled:
    "Klarna wasn't completed. Choose a payment method below to continue.",
  unconfirmed:
    "We didn't get a confirmation from Klarna. Choose a payment method below to continue.",
  failed:
    "Klarna didn't go through. Please choose a payment method below to continue.",
  expired:
    "Your Klarna session expired. Please choose a payment method below to continue.",
};

export function klarnaNoticeFor(status, { mode, timedOut = false } = {}) {
  switch (status) {
    case KLARNA_CHECKOUT_FAILED:
      return KLARNA_NOTICES.failed;
    case KLARNA_CHECKOUT_EXPIRED:
      return KLARNA_NOTICES.expired;
    case KLARNA_CHECKOUT_CANCELED:
      if (timedOut) return KLARNA_NOTICES.unconfirmed;
      return mode === KLARNA_RESUME_MODE
        ? KLARNA_NOTICES.left
        : KLARNA_NOTICES.canceled;
    default:
      return KLARNA_NOTICES.canceled;
  }
}

export default function KlarnaCheckoutReturn({
  attemptID,
  history,
  mode = KLARNA_RETURN_MODE,
  onResolved,
  polls = KLARNA_RETURN_POLLS,
  pollMs = KLARNA_RETURN_POLL_MS,
}) {
  // "checking" | "signin" | "missing" | "error"
  const [phase, setPhase] = useState("checking");
  const [retry, setRetry] = useState(0);
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  useEffect(() => {
    let stopped = false;
    let timer;
    let checks = 0;
    const client = KickClient.newAuthenticatedClient();
    const url = (action) =>
      `/klarna/checkout-attempts/${encodeURIComponent(attemptID)}/${action}`;

    // True once the attempt no longer needs this screen.
    const settle = (data, { timedOut = false } = {}) => {
      if (finishKlarnaCheckout(data, history)) return true;
      if (data.status === KLARNA_CHECKOUT_COMPLETE) {
        // Submitted, but the confirmation context could not be rebuilt here.
        window.location.assign("/me/portal");
        return true;
      }
      if (data.status === KLARNA_CHECKOUT_PENDING) return false;
      onResolvedRef.current?.({
        status: data.status,
        notice: klarnaNoticeFor(data.status, { mode, timedOut }),
      });
      return true;
    };
    const fail = (error) => {
      const status = error?.response?.status;
      if (status === 401) setPhase("signin");
      else if (status === 403 || status === 404) setPhase("missing");
      else setPhase("error");
    };
    const cancel = async ({ timedOut }) => {
      try {
        const { data } = await client.post(url("cancel"), {});
        if (stopped) return;
        if (!settle(data, { timedOut })) setPhase("error");
      } catch (error) {
        if (!stopped) fail(error);
      }
    };
    const check = async () => {
      try {
        const { data } = await client.post(url("resume"), {});
        if (stopped) return;
        if (settle(data)) return;
        if (++checks < polls) {
          timer = setTimeout(check, pollMs);
          return;
        }
        await cancel({ timedOut: true });
      } catch (error) {
        if (!stopped) fail(error);
      }
    };

    setPhase("checking");
    if (mode === KLARNA_RESUME_MODE) cancel({ timedOut: false });
    else check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [attemptID, history, mode, polls, pollMs, retry]);

  const retryButton = (label) => (
    <StatusLinkButton onClick={() => setRetry((n) => n + 1)}>
      {label}
    </StatusLinkButton>
  );

  let title;
  let message;
  let actions = null;
  let spinner = false;
  switch (phase) {
    case "signin":
      title = "Please sign in";
      message =
        "Your session expired. Sign in, then check again to finish your checkout.";
      actions = (
        <>
          <StatusButton
            component="a"
            href="/me/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            Sign in
          </StatusButton>
          {retryButton("Check again")}
        </>
      );
      break;
    case "missing":
      title = "We couldn't find this checkout";
      message = "Please return to your account and start your order again.";
      actions = (
        <>
          <StatusButton component="a" href="/me/portal">
            Go to my account
          </StatusButton>
          {retryButton("Try again")}
        </>
      );
      break;
    case "error":
      title = "We hit a snag";
      message = "We couldn't reach our payment provider. Please try again.";
      actions = (
        <StatusButton onClick={() => setRetry((n) => n + 1)}>
          Try again
        </StatusButton>
      );
      break;
    default:
      spinner = true;
      title =
        mode === KLARNA_RESUME_MODE
          ? "Loading your payment options"
          : "Finishing your checkout";
      message =
        mode === KLARNA_RESUME_MODE
          ? "Checking your Klarna session…"
          : "Confirming your payment details with Klarna…";
  }

  return (
    <CheckoutStatusCard
      spinner={spinner}
      title={title}
      message={message}
      actions={actions}
      testID="klarna-checkout-return"
    />
  );
}
